// Anchored popover (not a modal, not a dialog) -- used by TypeChooser
// (D-C, "click the type tag, should show top recommendations and an input
// box to search/add new tag", anchored to the tag rather than a long
// dropdown list). Same primitive family as dialog.tsx/dropdown-menu.tsx in
// this package; Radix's Popover already handles Escape-to-close,
// outside-click-to-close, and focus return to the trigger.
import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";

import { cn } from "./lib/utils";

const Popover = PopoverPrimitive.Root;
const PopoverTrigger = PopoverPrimitive.Trigger;
const PopoverAnchor = PopoverPrimitive.Anchor;
const PopoverClose = PopoverPrimitive.Close;

// 2026-09-08 redesign spec §11 "Popover defect": the content surface must
// be FULLY OPAQUE from the first interactive frame -- `bg-popover` already
// resolves to an opaque token, but the previous `fade-in-0`/`fade-out-0`
// utilities animated the whole content block's CSS `opacity` (not just a
// decorative element), so the underlying feed/merchant/date text showed
// through during the transition (screenshot 03/09). Fix: drop the
// opacity-fade utilities entirely and keep only a TRANSFORM-only open/close
// motion (`zoom-in-95`/`zoom-out-95` + a short slide), which never touches
// `opacity`/`background-color` alpha. `motion-reduce:` disables even that
// transform under `prefers-reduced-motion` (spec §8). Verified by a
// Playwright check asserting computed `background-color` alpha = 1 on the
// open transition's first rendered frame (see
// `frontend/e2e/screenshots.spec.ts`).
const POPOVER_CONTENT_MOTION =
  "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-top-1 data-[state=open]:slide-in-from-top-1 duration-150 motion-reduce:animate-none motion-reduce:transition-none";

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, align = "start", sideOffset = 6, ...props }, ref) => (
  <PopoverPrimitive.Portal>
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      className={cn(
        "z-50 w-72 rounded-lg border border-muted-foreground bg-popover p-0 text-popover-foreground opacity-100 shadow-overlay outline-none",
        POPOVER_CONTENT_MOTION,
        className,
      )}
      {...props}
    />
  </PopoverPrimitive.Portal>
));
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

export { Popover, PopoverTrigger, PopoverAnchor, PopoverClose, PopoverContent };
